// src/pages/Blog.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// ================= BLOG DATA =================
const blogPosts = [
  {
    id: 1,
    title: "What Are Hormones & Why They Matter for Women?",
    excerpt:
      "Hormones are powerful chemical messengers that influence energy, mood, weight, skin, sleep, and overall health.",
    image: "/assets/01.jpeg",
    category: "Hormonal Health",
    content: `
Hormones are chemical messengers in the body that help control how your body works. 
They travel through the bloodstream and send signals to organs and tissues, telling them 
what to do and when to do it.

For women, hormones play a crucial role in daily wellbeing — not just reproduction.

✨ Hormones help control:
• Energy levels
• Mood and emotional balance
• Metabolism and weight
• Skin, hair, and nail health
• Sleep cycles
• Menstrual health
• Stress response

When hormones are balanced, the body functions smoothly. 
When they are imbalanced, women may experience fatigue, mood swings, acne, hair fall, 
irregular cycles, or weight changes.

Understanding hormones is the first step toward better self-care, wellness, and long-term health.
    `,
    shopLink: "/category/wellness",
  },
  {
    id: 2,
    title: "Signs of Hormonal Imbalance Every Woman Should Know",
    excerpt:
      "From fatigue to acne, learn the most common signs your hormones may be out of balance.",
    image: "/assets/2.png",
    category: "Hormonal Awareness",
    content: `
Hormonal imbalance can show up in subtle ways and is often ignored.

Common signs include:
• Constant fatigue
• Mood swings or anxiety
• Acne or sudden skin changes
• Weight gain without diet changes
• Irregular periods
• Hair thinning or hair fall
• Poor sleep quality

Lifestyle, stress, diet, and nutrient deficiencies all affect hormones.
Small daily changes can make a big difference when done consistently.
    `,
    shopLink: "/category/wellness",
  },
  {
    id: 3,
    title: "Hormones That Control Weight, Mood & Energy",
    excerpt:
      "Weight, energy, and mood aren’t just about diet or exercise — hormones play a major role.",
    image: "/assets/3.png",
    category: "Hormones & Metabolism",
    content: `
Hormones are chemical messengers that regulate how your body stores fat, burns calories, maintains energy, and even affects mood. When key hormones are out of balance, it can impact not just your weight but also how you feel physically and emotionally.

Some of the main hormones that influence weight, energy, and mood include:
    `,
    shopLink: "/category/fitness",
  },
  {
    id: 4,
    title: "Daily Habits That Support Healthy Hormones",
    excerpt:
      "Simple lifestyle habits that naturally support hormone balance.",
    image: "/assets/4.png",
    category: "Lifestyle",
    content: `
Healthy hormones are built through daily habits, not quick fixes.

Helpful habits include:
• Consistent sleep schedule
• Balanced meals with protein
• Light daily movement
• Managing stress levels
• Reducing processed foods
• Staying hydrated

These habits support long-term wellness and hormonal stability.
    `,
    shopLink: "/category/wellness",
  },
  {
    id: 5,
    title: "Hormone-Friendly Supplements Women Love",
    excerpt:
      "Popular supplements women use to support energy, mood, and overall wellness.",
    image: "/assets/5.png",
    category: "Supplements",
    content: `
Many women turn to supplements to support hormone health alongside lifestyle changes.

Commonly used supplements include:
• Magnesium
• Vitamin D
• Omega-3
• Inositol
• Multivitamins for women

Always choose high-quality products and consult a professional if needed.
    `,
    shopLink: "/category/supplements",
  },
];

export default function Blog() {
  const [activePost, setActivePost] = useState(null);
  const navigate = useNavigate();

  return (
    <div>
      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-purple-50 py-20 px-6 text-center">
        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-800">
          Glow Guides for Women
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-gray-600 text-lg">
          Easy-to-understand hormone, wellness, and lifestyle guides designed to
          educate, inspire, and support women.
        </p>
      </section>

      {/* ================= BLOG GRID ================= */}
<section className="max-w-7xl mx-auto px-6 py-16">
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
    {blogPosts.map((post) => (
      <div
        key={post.id}
        onClick={() => {
          // Navigate to dedicated pages for certain posts
          if (post.id === 1) {
            navigate("/what-are-hormones");
          } else if (post.id === 2) {
            navigate("/Signs");
          } else if (post.id === 3) {
            navigate("/Hormones");
          } else if (post.id === 4) {
            navigate("/Lifestyle");
          } else if (post.id === 5) {
            navigate("/Supplements");
          } else {
            // fallback: open modal for other posts
            setActivePost(post);
          }
        }}
        className="bg-white rounded-2xl shadow hover:shadow-xl transition overflow-hidden flex flex-col cursor-pointer"
      >
        <div className="h-54 overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover hover:scale-105 transition duration-500"
          />
        </div>

        <div className="p-6 flex flex-col flex-grow">
          <span className="text-xs uppercase tracking-wide text-pink-500 font-semibold mb-2">
            {post.category}
          </span>

          <h3 className="font-semibold text-lg text-gray-800 mb-3">
            {post.title}
          </h3>

          <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>

          {/* Optional small button can stay, but now card click works */}
          <span className="mt-auto text-pink-600 font-medium text-sm hover:underline">
            Read Full Guide →
          </span>
        </div>
      </div>
    ))}
  </div>
</section>


      {/* ================= MODAL ================= */}
      {activePost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto relative shadow-2xl">
            <button
              onClick={() => setActivePost(null)}
              className="absolute top-3 left-3 text-gray-800 hover:text-pink-600 text-3xl font-bold"
            >
              &times;
            </button>

            <img
              src={activePost.image}
              alt={activePost.title}
              className="w-full h-48 object-cover rounded-t-2xl"
            />

            <div className="p-6 flex flex-col gap-5">
              <h2 className="text-2xl font-bold text-gray-800">
                {activePost.title}
              </h2>

              <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                {activePost.content}
              </p>

              {/* AMAZON CTA */}
              <button
                onClick={() => {
                  navigate(activePost.shopLink);
                  setActivePost(null);
                }}
                className="mt-4 bg-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-pink-600 transition"
              >
                Shop Recommended Products →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= DISCLAIMER ================= */}
      <section className="bg-pink-50 py-12 px-6 text-center">
        <p className="max-w-3xl mx-auto text-sm text-gray-600">
          This content is for educational purposes only and does not replace
          professional medical advice. As an Amazon Associate, Glow Picks may earn
          from qualifying purchases.
        </p>
      </section>
    </div>
  );
}
